import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Layers, Save, Plus, Trash2 } from 'lucide-react';

const ALL_CATEGORIES = ['sheet', 'tile', 'ridge', 'valley', 'barge', 'spouting', 'fastening', 'flashing', 'cleaning', 'recoat', 'aquaseal', 'safety', 'travel', 'urgent', 'misc', 'underlay'];
const CATEGORY_LABELS = {
  sheet: 'Sheet', tile: 'Tiles', ridge: 'Ridge', valley: 'Valley', barge: 'Barge',
  spouting: 'Spouting', fastening: 'Fastening', flashing: 'Flashing', cleaning: 'Cleaning',
  recoat: 'Recoat', aquaseal: 'Dektite', safety: 'Safety', travel: 'Travel',
  urgent: 'Urgent', misc: 'Misc', underlay: 'Underlay',
};

export default function RoofTypeMaterialsPage() {
  const { api } = useAuth();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editCats, setEditCats] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/roof-type-materials');
      setMappings(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (m) => {
    setEditId(m._id);
    setEditCats([...(m.categories || [])]);
  };

  const toggleCat = (cat) => {
    setEditCats(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };

  const saveMapping = async (m) => {
    setSaving(true);
    try {
      await api.put(`/roof-type-materials/${m._id}`, { roof_type: m.roof_type, categories: editCats });
      setEditId(null);
      load();
    } catch (e) { alert('Failed to save'); }
    setSaving(false);
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Loading...</div>;

  return (
    <div data-testid="roof-type-materials-page" className="space-y-5">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Materials by Roof Type</h1>
        <p className="text-sm text-slate-500 mt-1">Configure which material categories are available for each roof type. This controls the product filtering in the Quote Builder.</p>
      </div>

      <div className="bg-white border border-[#E2E2DF] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 w-48">Roof Type</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Material Categories</th>
              <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 w-24">Actions</th>
            </tr>
          </thead>
          <tbody>
            {mappings.map(m => (
              <tr key={m._id} className="border-t border-[#E2E2DF] hover:bg-slate-50/50">
                <td className="px-4 py-3">
                  <span className="text-sm font-semibold text-slate-800">{m.roof_type}</span>
                </td>
                <td className="px-4 py-3">
                  {editId === m._id ? (
                    <div className="flex flex-wrap gap-1.5">
                      {ALL_CATEGORIES.map(cat => (
                        <button key={cat} onClick={() => toggleCat(cat)} className={`text-xs px-2.5 py-1 rounded-full border transition-all ${editCats.includes(cat) ? 'bg-[#D35400] text-white border-[#D35400]' : 'border-[#E2E2DF] text-slate-400 hover:border-slate-400'}`}>
                          {CATEGORY_LABELS[cat] || cat}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {(m.categories || []).map(cat => (
                        <span key={cat} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{CATEGORY_LABELS[cat] || cat}</span>
                      ))}
                      <span className="text-[11px] text-slate-400 self-center ml-1">({(m.categories || []).length})</span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {editId === m._id ? (
                    <div className="flex gap-1 justify-center">
                      <button data-testid={`save-mapping-${m._id}`} onClick={() => saveMapping(m)} disabled={saving} className="p-1.5 text-green-600 hover:bg-green-50 rounded"><Save size={14} /></button>
                      <button onClick={() => setEditId(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded text-xs">Cancel</button>
                    </div>
                  ) : (
                    <button data-testid={`edit-mapping-${m._id}`} onClick={() => startEdit(m)} className="text-xs text-[#D35400] font-semibold hover:text-[#A04000]">Edit</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
